import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './pages/admin/Layout';
import ExamManagement from './pages/admin/ExamQuestionManagement';
import Login from './pages/admin/Login';
import UserLogin from './pages/user/Login';
import { isAdmin, isAuthenticated, logout } from './services/authService';
import Home from './pages/user/Home';
import UserManagement from './pages/admin/UserManagement';
import Exam from './pages/user/Exam';
import ExamInfor from './pages/user/ExamInfo';
import QuestionManagement from './pages/admin/QuestionManagement';
import VideoManagement from './pages/admin/VideoManagement';
import Video from './pages/user/Video';
import SkillManagement from './pages/admin/SkillManagement';
import TermsAndPolicy from './pages/user/TermsAndPolicy';
import PrivacyManagement from './pages/admin/PrivacyManagement';
import LinkDocumentManagement from './pages/admin/LinkDocumentManagement';
import CourseManagement from './pages/admin/CourseManagement';
import DocumentManagement from './pages/admin/DocumentManagement';
import Introduction from './pages/user/Introduction';

// Hàm kiểm tra token có hết hạn không
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    const payload = JSON.parse(window.atob(base64));

    return payload.exp * 1000 < Date.now();
  } catch (error) {
    return true;
  }
};

// Interceptor để kiểm tra token
const setupAxiosInterceptors = (navigate) => {
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');

      if (token && isTokenExpired(token)) {
        // Nếu token hết hạn, xóa token và chuyển về trang login
        logout();
        navigate('/login');
        return Promise.reject(new Error('Token expired'));
      }

      // Thêm token vào header nếu còn hiệu lực
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Xử lý lỗi 401 (Unauthorized)
      if (error.response && error.response.status === 401) {
        logout();
        navigate('/login');
      }
      return Promise.reject(error);
    }
  );
};

// Wrapper để setup interceptors
const AuthInterceptorWrapper = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    setupAxiosInterceptors(navigate);
  }, [navigate]);

  return <>{children}</>;
};

// Route bảo vệ
const PrivateRoute = ({ children, adminOnly = false }) => {
  const auth = isAuthenticated();
  const admin = isAdmin();

  // Kiểm tra token trước khi cho phép truy cập
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired(token)) {
    logout();
    return <Navigate to="/login" />;
  }

  if (!auth) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !admin) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthInterceptorWrapper>
        <Routes>
          <Route path="/admin/login" element={<Login />} />
          <Route path="/login" element={<UserLogin />} />
          <Route path="/introduction" element={<Introduction />} />
          <Route path="/practice-info/:id" element={<ExamInfor type='public' />} />
          <Route path="/practice-test" element={<Exam type='public' />} />
          {/* Route Home cho user đã đăng nhập */}
          <Route path="/" element={<PrivateRoute> <Home /> </PrivateRoute>} />
          <Route path="/exam-info" element={<PrivateRoute> <ExamInfor type='private' /> </PrivateRoute>} />
          <Route path="/exam" element={<PrivateRoute> <Exam type='private' /> </PrivateRoute>} />
          <Route path="/video" element={<PrivateRoute> <Video /> </PrivateRoute>} />
          <Route path="/terms-and-policy" element={<PrivateRoute> <TermsAndPolicy /> </PrivateRoute>} />
          {/* Route Admin Dashboard chỉ dành cho admin */}
          <Route
            path="/admin"
            element={
              <PrivateRoute adminOnly={true}>
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route path="/admin/exams" element={<ExamManagement />} />
            <Route path="/admin/questions" element={<QuestionManagement />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/videos" element={<VideoManagement />} />
            <Route path="/admin/skills" element={<SkillManagement />} />
            <Route path="/admin/privacy" element={<PrivacyManagement />} />
            <Route path="/admin/link-document" element={<LinkDocumentManagement />} />
            <Route path="/admin/courses" element={<CourseManagement />} />
            <Route path="/admin/documents" element={<DocumentManagement />} />
          </Route>
        </Routes>
      </AuthInterceptorWrapper>
    </BrowserRouter>
  );
}

export default App;